import _ from 'underscore';
import { getRangesFromData } from '@bingads-webui-universal/temporal-utitlities';
import { odataErrorCodes } from '@bingads-webui-campaign/instrumentation';
import { CSMLSortBy, getErrorString } from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

export const createFolderFilterQuery = ({
  searchTerm,
  minWidth,
  minHeight,
  isVideoDurationBoundaryIncluded = false,
  minVideoDuration,
  maxVideoDuration,
  dateAddedStart,
  dateAddedEnd,
  assetType,
  sourceType,
  assetSources,
  folderId,
  hasPerfData,
  topRated,
}) => {
  const filterPredicates = [];

  if (assetType) {
    filterPredicates.push(`FolderAssetType eq '${assetType}'`);
  }
  if (minWidth) {
    filterPredicates.push(`Width gt ${minWidth}`);
  }
  if (minHeight) {
    filterPredicates.push(`Height gt ${minHeight}`);
  }

  if (minVideoDuration) {
    if (isVideoDurationBoundaryIncluded) {
      filterPredicates.push(`VideoDuration ge ${minVideoDuration}`);
    } else {
      filterPredicates.push(`VideoDuration gt ${minVideoDuration}`);
    }
  }
  if (maxVideoDuration) {
    if (isVideoDurationBoundaryIncluded) {
      filterPredicates.push(`VideoDuration le ${maxVideoDuration}`);
    } else {
      filterPredicates.push(`VideoDuration lt ${maxVideoDuration}`);
    }
  }

  if (dateAddedStart && dateAddedEnd && dateAddedStart === dateAddedEnd) {
    filterPredicates.push(`CreatedTime eq ${dateAddedStart}`);
  } else {
    if (dateAddedStart) {
      filterPredicates.push(`CreatedTime ge ${dateAddedStart}`);
    }
    if (dateAddedEnd) {
      filterPredicates.push(`CreatedTime le ${dateAddedEnd}`);
    }
  }
  if (sourceType) {
    filterPredicates.push(`SourceType eq '${sourceType}'`);
  }
  if (!_.isEmpty(assetSources)) {
    filterPredicates.push(`AssetSourceType in (${assetSources.map(s => `'${s}'`).join(',')})`);
  }
  if (searchTerm) {
    filterPredicates.push(`contains(BroadSearchFields, '${searchTerm}')`);
  }

  if (folderId) {
    filterPredicates.push(`FolderId eq ${folderId}`);
  }

  if (hasPerfData) {
    filterPredicates.push('PerformanceMetrics/Impressions gt 0');
  }

  if (topRated) {
    filterPredicates.push("AssetPerformanceRating eq 'Best'");
  }

  const filterQuery = filterPredicates.length > 0 ?
    `${filterPredicates.join(' and ')}` : '';

  return filterQuery ? `$filter=${filterQuery}` : '';
};

const formatCivilDateString = (civilDate) => {
  if (!civilDate) return '';

  const { year, month, day } = civilDate;
  if (!year || !month || !day) return '';

  return `${year}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`;
};

export const getDateRangeParams = (i18n, dateData, dateFormat, lcid) => {
  const { selectedRange, selectedComparedRange } = getRangesFromData(i18n, dateData, dateFormat, lcid) || {};
  if (selectedRange) {
    const startParamStr = formatCivilDateString(selectedRange.start);
    const endParamStr = formatCivilDateString(selectedRange.end);
    const comparisonStartDateStr = selectedComparedRange ? formatCivilDateString(selectedComparedRange.start) : null;
    const comparisonEndDateStr = selectedComparedRange ? formatCivilDateString(selectedComparedRange.end) : null;

    return {
      startDate: startParamStr,
      endDate: endParamStr,
      comparisonStartDate: comparisonStartDateStr,
      comparisonEndDate: comparisonEndDateStr,
    };
  }
  return {};
};

// dateStr: string in format like 2022-06-21T08:55:59.807-07:00
export const parseMTDateString = (i18n, dateStr) => {
  if (!dateStr) {
    return null;
  }
  // remove the millisecs part to avoid parsing fail (the length of millisecs part is variable)
  const str = dateStr.replace(/\.\d+/, '');
  const options = {
    raw: 'yyyy-MM-ddTHH:mm:ssZZZZZ',
    timeZone: null,
  };
  const date = i18n.parseDate(str, options);
  return date;
};

export const CSMLSortingRequestParams = {
  [CSMLSortBy.MostRecentAsc]: { sortBy: 'ModifiedTime', sortDirection: 'asc' },
  [CSMLSortBy.MostRecent]: { sortBy: 'ModifiedTime', sortDirection: 'desc' },

  [CSMLSortBy.ImageName]: { sortBy: 'Text', sortDirection: 'asc' },
  [CSMLSortBy.ImageNameDesc]: { sortBy: 'Text', sortDirection: 'desc' },
  [CSMLSortBy.LastCreated]: { sortBy: 'CreatedTime', sortDirection: 'desc' },
  [CSMLSortBy.LastCreatedAsc]: { sortBy: 'CreatedTime', sortDirection: 'asc' },

  [CSMLSortBy.AssetIdAsc]: { sortBy: 'AssetId', sortDirection: 'asc' },
  [CSMLSortBy.AssetIdDesc]: { sortBy: 'AssetId', sortDirection: 'desc' },

  [CSMLSortBy.ClickAsc]: { sortBy: 'PerformanceMetrics/Clicks', sortDirection: 'asc' },
  [CSMLSortBy.ClickDesc]: { sortBy: 'PerformanceMetrics/Clicks', sortDirection: 'desc' },
  [CSMLSortBy.ClickPeriodAsc]: { sortBy: 'PoPPerformanceMetrics/Clicks', sortDirection: 'asc' },
  [CSMLSortBy.ClickPeriodDesc]: { sortBy: 'PoPPerformanceMetrics/Clicks', sortDirection: 'desc' },
  [CSMLSortBy.ClickChangeAsc]: { sortBy: 'PoPPerformanceMetricsChange/Clicks', sortDirection: 'asc' },
  [CSMLSortBy.ClickChangeDesc]: { sortBy: 'PoPPerformanceMetricsChange/Clicks', sortDirection: 'desc' },
  [CSMLSortBy.ClickChangePercentAsc]: { sortBy: 'PoPPerformanceMetricsChangePercentage/Clicks', sortDirection: 'asc' },
  [CSMLSortBy.ClickChangePercentDesc]: { sortBy: 'PoPPerformanceMetricsChangePercentage/Clicks', sortDirection: 'desc' },

  [CSMLSortBy.CTRAsc]: { sortBy: 'PerformanceMetrics/CTR', sortDirection: 'asc' },
  [CSMLSortBy.CTRDesc]: { sortBy: 'PerformanceMetrics/CTR', sortDirection: 'desc' },
  [CSMLSortBy.CTRPeriodAsc]: { sortBy: 'PoPPerformanceMetrics/CTR', sortDirection: 'asc' },
  [CSMLSortBy.CTRPeriodDesc]: { sortBy: 'PoPPerformanceMetrics/CTR', sortDirection: 'desc' },
  [CSMLSortBy.CTRChangeAsc]: { sortBy: 'PoPPerformanceMetricsChange/CTR', sortDirection: 'asc' },
  [CSMLSortBy.CTRChangeDesc]: { sortBy: 'PoPPerformanceMetricsChange/CTR', sortDirection: 'desc' },
  [CSMLSortBy.CTRChangePercentAsc]: { sortBy: 'PoPPerformanceMetricsChangePercentage/CTR', sortDirection: 'asc' },
  [CSMLSortBy.CTRChangePercentDesc]: { sortBy: 'PoPPerformanceMetricsChangePercentage/CTR', sortDirection: 'desc' },

  [CSMLSortBy.ImpressionAsc]: { sortBy: 'PerformanceMetrics/Impressions', sortDirection: 'asc' },
  [CSMLSortBy.ImpressionDesc]: { sortBy: 'PerformanceMetrics/Impressions', sortDirection: 'desc' },
  [CSMLSortBy.ImpressionPeriodAsc]: { sortBy: 'PoPPerformanceMetrics/Impressions', sortDirection: 'asc' },
  [CSMLSortBy.ImpressionPeriodDesc]: { sortBy: 'PoPPerformanceMetrics/Impressions', sortDirection: 'desc' },
  [CSMLSortBy.ImpressionChangeAsc]: { sortBy: 'PoPPerformanceMetricsChange/Impressions', sortDirection: 'asc' },
  [CSMLSortBy.ImpressionChangeDesc]: { sortBy: 'PoPPerformanceMetricsChange/Impressions', sortDirection: 'desc' },
  [CSMLSortBy.ImpressionChangePercentAsc]: { sortBy: 'PoPPerformanceMetricsChangePercentage/Impressions', sortDirection: 'asc' },
  [CSMLSortBy.ImpressionChangePercentDesc]: { sortBy: 'PoPPerformanceMetricsChangePercentage/Impressions', sortDirection: 'desc' },

  [CSMLSortBy.SpendAsc]: { sortBy: 'PerformanceMetrics/Spend', sortDirection: 'asc' },
  [CSMLSortBy.SpendDesc]: { sortBy: 'PerformanceMetrics/Spend', sortDirection: 'desc' },
  [CSMLSortBy.SpendPeriodAsc]: { sortBy: 'PoPPerformanceMetrics/Spend', sortDirection: 'asc' },
  [CSMLSortBy.SpendPeriodDesc]: { sortBy: 'PoPPerformanceMetrics/Spend', sortDirection: 'desc' },
  [CSMLSortBy.SpendChangeAsc]: { sortBy: 'PoPPerformanceMetricsChange/Spend', sortDirection: 'asc' },
  [CSMLSortBy.SpendChangeDesc]: { sortBy: 'PoPPerformanceMetricsChange/Spend', sortDirection: 'desc' },
  [CSMLSortBy.SpendChangePercentAsc]: { sortBy: 'PoPPerformanceMetricsChangePercentage/Spend', sortDirection: 'asc' },
  [CSMLSortBy.SpendChangePercentDesc]: { sortBy: 'PoPPerformanceMetricsChangePercentage/Spend', sortDirection: 'desc' },
};

// get date string with offset, such as: '2022-01-01+08:00'
export const formatCivilDate = (i18n, civilDate, timeZone, utcOffset, timeStr) => {
  const rawFormat = timeStr ? `yyyy-MM-ddT${timeStr}` : 'yyyy-MM-dd';
  const dateStr = i18n.formatCivilDate(civilDate, { raw: rawFormat, timeZone });
  // encode '+' char
  const offset = utcOffset ? utcOffset.replace('+', '%2B') : '';
  return `${dateStr}${offset}`;
};

export const isErrorWhitelisted = (error = {}) => {
  if (error.errors && error.errors.length === 1) {
    const errorCode = error.errors[0].Code;

    if (_.isString(errorCode)) {
      return _.contains(odataErrorCodes, errorCode.toUpperCase());
    }
  }
  return false;
};

export const handleErrorLogging = (err, api, logger) => {
  if (isErrorWhitelisted(err)) {
    logger.logTrace(`[IgnoredDataServiceError] ${getErrorString(err)}`, { api });
  } else {
    logger.logError(err, null, api);
  }
};

// eslint-disable-next-line no-unused-vars
export const removeNullUndefined = (obj) => {
  const newObj = {};
  _.keys(obj).forEach((key) => {
    if (_.isObject(obj[key])) {
      newObj[key] = removeNullUndefined(obj[key]);
    } else if (obj[key] !== null && obj[key] !== undefined) {
      newObj[key] = obj[key];
    }
  });
  return newObj;
};
